import type { ChatroomDataObject } from '#models/services';
import { useMutation } from '#react-query';
import { addMemberToChatroom, deleteMemberFromChatroom } from '../../services';
import { useUpdateChatroomInCache } from '../cache';

type ChatroomMember = {
  _id: string;
  participant: ChatroomMemberParticipant;
};

type ChatroomMemberParticipant = {
  email: string;
  firstName: string;
  lastName: string;
};

type ChatroomMemberToRemove = {
  _id: string;
};

type ManageChatroomMembers = { chatroomId: string } & (
  | {
      membersToAdd: ChatroomMember[];
      membersToRemove: ChatroomMemberToRemove[];
    }
  | {
      membersToAdd: ChatroomMember[];
      membersToRemove?: never;
    }
  | {
      membersToAdd?: never;
      membersToRemove: ChatroomMemberToRemove[];
    }
);

const BATCH_SIZE = 10;

type ChatroomResponse = Awaited<ReturnType<typeof addMemberToChatroom>>;

const getResponseWithNewestUpdatedAt = (
  results: Array<PromiseSettledResult<ChatroomResponse>>,
  current?: ChatroomResponse,
) => {
  let newest = current;

  for (const result of results) {
    if (result.status !== 'fulfilled') {
      continue;
    }

    if (!newest || (result.value.data.updatedAt ?? '') > (newest.data.updatedAt ?? '')) {
      newest = result.value;
    }
  }

  return newest;
};

export const useManageChatroomMembersMutation = () => {
  const updateChatroomInCache = useUpdateChatroomInCache();

  return useMutation<ChatroomDataObject | undefined, Error, ManageChatroomMembers>({
    async mutationFn(params: ManageChatroomMembers) {
      const { chatroomId, membersToAdd = [], membersToRemove = [] } = params;

      let lastResponse;

      for (let index = 0; index < membersToAdd.length; index += BATCH_SIZE) {
        const batch = membersToAdd.slice(index, index + BATCH_SIZE);
        // eslint-disable-next-line no-await-in-loop -- Batches must run sequentially to avoid overloading the API
        const results = await Promise.allSettled(
          batch.map(async ({ _id, participant }) =>
            addMemberToChatroom({
              body: {
                id: _id,
                participant,
              },
              routeParams: { chatroomId },
            }),
          ),
        );
        lastResponse = getResponseWithNewestUpdatedAt(results, lastResponse);
      }

      for (let index = 0; index < membersToRemove.length; index += BATCH_SIZE) {
        const batch = membersToRemove.slice(index, index + BATCH_SIZE);
        // eslint-disable-next-line no-await-in-loop -- Batches must run sequentially to avoid overloading the API
        const results = await Promise.allSettled(
          batch.map(async ({ _id }) => deleteMemberFromChatroom({ routeParams: { chatroomId, memberId: _id } })),
        );
        lastResponse = getResponseWithNewestUpdatedAt(results, lastResponse);
      }

      return lastResponse ? lastResponse.data : undefined;
    },
    onSuccess(newChatroom) {
      if (newChatroom) {
        updateChatroomInCache({ newChatroom });
      }
    },
  });
};
